import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CardMetrics from './../../components/CardMetrics';
import EditableInput from '../../components/EditableInput';
import EditableSelect from './../../components/EditableSelect';
import UneditableInput from '../../components/UneditableInput';
import EditableDateInput from '../../components/EditableDateInput';

import {
  getIssues,
  updateIssue,
  getIssueMetrics,
  getIssueInHtml,
  resendIssue,
  getIssueChangelog,
  editIssue,
  getAllCategoriesName,
  assignCategoryToIssue,
  clearCacheAndUpdateIssueLanguage,
} from '../../services/issue';
import { showAlert } from '../../store/ducks/alert';

import {
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CardContent,
  Card,
} from '@material-ui/core';
import { Save, Send, Backup, Sync, ExpandMore } from '@material-ui/icons';
import Select from 'react-select';
import moment from 'moment';

const IssuesInformation = props => {
  const dispatch = useDispatch();
  const [issue, setIssue] = useState({});
  const [issueBeforeUpdate, setIssueBeforeUpdate] = useState({});
  const [issueMetrics, setIssueMetrics] = useState({});
  const [issueInHtml, setIssueInHtml] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isLoadingChangelog, setIsLoadingChangelog] = useState(false);
  const [changelogData, setChangelogData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [defaultValue, setDefaultValue] = useState({ value: '', label: '' });

  useEffect(() => {
    displayIssue();
    handleCategories();
  }, []);

  useEffect(() => {
    if (issue.categoryId) {
      for (const item of categories) {
        if (item.value === issue.categoryId) {
          setDefaultValue(item);
        }
      }
    }
  }, issue.categoryId);

  const handleUpdate = async e => {
    e.preventDefault();

    try {
      if (issue?.accessLevel) delete issue.accessLevel;
      await updateIssue(issue.id, issue);
      if (issue.language !== issueBeforeUpdate.language) {
        await clearCacheAndUpdateIssueLanguage(issue.id, issue.language);
      }
      dispatch(showAlert('success', 'Issue has been updated! 🔥'));
    } catch (error) {
      dispatch(showAlert('error', 'Error! We could not update the Issue. 😔'));
      console.log(error);
    }
  };

  const displayIssue = async () => {
    const issueId = props.match.params.uid;
    try {
      setIsLoading(true);
      const { data } = await getIssues(0, { searchItem: 'uid', search: issueId });
      const metrics = await getIssueMetrics(issueId);
      try {
        const html = await getIssueInHtml(issueId);
        setIssueInHtml(html.data);
      } catch {
        setIssueInHtml(null);
      }
      setIssue(data.rows[0]);
      setIssueBeforeUpdate(data.rows[0]);
      setIssueMetrics(metrics.data);
      setIsLoading(false);
    } catch (error) {
      dispatch(showAlert('error', 'Ops! We could not load this Issue. 😔'));
      console.log(error);
    }
  };

  const handleResend = async () => {
    if (!issue.id || !issue.scheduledTo) {
      dispatch(showAlert('error', 'Ops! We could Resend this issue. 😔'));
      return;
    }
    if (isResending) {
      dispatch(showAlert('error', 'You Already Requested That Action. Do not try again! 😔'));
      return;
    }
    try {
      setIsResending(true);
      await resendIssue(issue.id);
      dispatch(showAlert('success', 'Sending to who may not received'));
    } catch (error) {
      dispatch(showAlert('error', 'Ops! Something Went Wrong. 😔'));
    }
  };

  const getChangelogData = async () => {
    try {
      setIsLoadingChangelog(true);
      const { data: response } = await getIssueChangelog(issue.uid);
      setChangelogData(response);
      setIsLoadingChangelog(false);
    } catch (error) {
      setIsLoadingChangelog(true);
      dispatch(showAlert('error', 'Ops! Something Went Wrong. 😔'));
    }
  };

  const handleEditIssue = async data => {
    try {
      setIsLoading(true);
      await editIssue(issue.uid, { issue: data });
      await displayIssue();
    } catch (error) {
      dispatch(showAlert('error', 'Ops! Something Went Wrong. 😔'));
    }
  };

  const handleCategories = async () => {
    try {
      let { data } = await getAllCategoriesName();

      data = Object.values(data).map(item => ({
        value: item.id,
        label: item.name,
      }));
      setCategories(data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdateCategory = async inputValue => {
    try {
      await assignCategoryToIssue(props.match.params.uid, inputValue.value);
    } catch (error) {
      dispatch(showAlert('error', 'Ops! Something Went Wrong. 😔'));
    }
  };

  const editable = ['title', 'subject', 'description','uniquePrice', 'bannerUrl', 'slug', 'utmCampaign', 'utmTerm', 'language'];

  const select = [
    'forMembers',
    'forNonMembers',
    'onlyForSubscribersOrMembers',
    'isPublished',
    'isSent',
    'isSendScheduled',
    'isPublishScheduled',
    'isMigratedFromAnotherSource',
    'deleted',
  ];

  const uneditable = ['id', 'channelId', 'userId', 'uid', 'createdAt', 'updatedAt'];

  if (isLoading) return <CircularProgress color="inherit" className="circular-progress" />;

  return (
    <>
      <div className="page-header">
        <h1>{issue.subject}</h1>
        <FormControlLabel
          control={
            <Switch
              className="enabled-switch"
              checked={issue.isPublic}
              onChange={e => setIssue({ ...issue, [e.target.name]: e.target.checked })}
              name="isPublic"
              color="primary"
            />
          }
          label="Public"
          labelPlacement="start"
        />
      </div>
      <section className="metrics-section">
        <h3>Issue Data and Metrics</h3>
        <CardMetrics data={issueMetrics} data-testid="metricsbox" />
      </section>
      <section className="update-section">
        <h3>Edit Issue</h3>
        <form onSubmit={handleUpdate} className="update-form">
          <div className="form-fields">
            <EditableDateInput data={issue} setData={setIssue} labels={['sentDate', 'publishedDate',]} />
            <EditableInput data={issue} setData={setIssue} labels={editable} />
            <EditableSelect data={issue} setData={setIssue} labels={select} />
            <EditableSelect data={issue} setData={setIssue} labels={['deletedAt']} isDeletedAt />
            <UneditableInput data={issue} labels={uneditable} />
          </div>
          <Button type="submit" className="update-btn" variant="contained" startIcon={<Save />}>
            Update Issue
          </Button>
        </form>
      </section>
      <section className="update-section">
        <h3>Category</h3>
        <div className="category-select">
          <Select
            options={categories}
            defaultValue={defaultValue}
            isSearchable
            placeholder={'Search a category...'}
            onChange={handleUpdateCategory}
          />
        </div>
      </section>
      <section className="update-section">
        {!changelogData && (
          <Button
            type="submit"
            className="update-btn"
            variant="contained"
            startIcon={!isLoadingChangelog ? <Backup /> : <Sync />}
            onClick={() => getChangelogData()}
          >
            {!isLoadingChangelog ? 'Retrieve Last Changes' : 'Loading Changelog...'}
          </Button>
        )}
        {changelogData && (
          <>
            <div className="container">
              <h4>Last Update: </h4>
              <span>{moment(changelogData.lastUpdate).format('LLL')}</span>
            </div>
            <div>
              {changelogData.changes && Object.keys(changelogData.changes).length > 0 && (
                <>
                  {Object.keys(changelogData.changes).map((item, index) => (
                    <Accordion key={index}>
                      <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                        Change #{item} -{' '}
                        {changelogData.changes && changelogData.changes[item] && changelogData.changes[item]['time']
                          ? moment(changelogData.changes[item]['time']).format('LLL')
                          : ''}
                      </AccordionSummary>
                      <AccordionDetails>
                        <ul className="changelog_list">
                          <li>
                            <Button
                              type="submit"
                              className="update-btn"
                              variant="contained"
                              startIcon={<Save />}
                              onClick={() => handleEditIssue(changelogData.changes[item])}
                            >
                              Recover from that changelog
                            </Button>
                          </li>
                          {changelogData.changes[item] &&
                            changelogData.changes[item]['blocks'] &&
                            changelogData.changes[item]['blocks'].length > 0 &&
                            changelogData.changes[item]['blocks'].map((item, index) => (
                              <li key={index} className="changelog_list_item">
                                <Card>
                                  <CardContent>
                                    <div className="changelog_item_wrapper">
                                      {item.data && Object.keys(item.data).length > 0 && (
                                        <table className="changelog_item_table">
                                          <thead className="changelog_item_header">
                                            <h4 className="changelog_item_title">{item.type}</h4>
                                            <tr>
                                              {Object.keys(item.data).map((i, j) => (
                                                <th key={j}>{i}</th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr>
                                              {Object.values(item.data).map((i, j) => (
                                                <td key={j}>{JSON.stringify(i)}</td>
                                              ))}
                                            </tr>
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              </li>
                            ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </>
              )}
            </div>
          </>
        )}
      </section>

      {issueInHtml && (
        <section className="render-issue-section">
          <h3>Issue View</h3>
          <div dangerouslySetInnerHTML={{ __html: issueInHtml.message }}></div>
        </section>
      )}

      <Button onClick={handleResend} disabled={isResending} className="update-btn" variant="contained" startIcon={<Send />}>
        Resend Issue For Who May Not Received
      </Button>
    </>
  );
};

export default IssuesInformation;
